// src/components/auth/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    updateProfile,
    updateEmail,
    updatePassword,
    reauthenticateWithCredential,
    EmailAuthProvider,
    sendEmailVerification,
    sendPasswordResetEmail,
    setPersistence,
    browserLocalPersistence,
    browserSessionPersistence,
} from 'firebase/auth';
import { 
    doc, 
    setDoc, 
    getDoc, 
    updateDoc, 
    serverTimestamp,
    collection,
    query,
    where,
    getDocs,
    arrayUnion,
    arrayRemove,
    deleteDoc,
    onSnapshot,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { auth, db } from '../../config/firebase';  // Fixed path
import {
    getIntegrationSettings,
    sendTransactionalEmail,
    sendOperationEmail,
    approveCompanyJoinRequest,
    updateCompanyProfile as updateCompanyProfileBackend,
    sendProfessionalVerificationEmail,
    getEmailVerificationStatus,
    requestProfessionalPasswordResetEmail,
    createCompanyMember,
    getPublicPlatformRuntime,
    requestWhatsAppOtp,
    verifyWhatsAppOtp,
} from '../../services/integrations/backend';
import { getPlatformLogoUrl, resolveCompanyId } from '../../utils/companyIdentity';
import { registerPushToken } from '../../services/integrations/push';
import { friendlyError } from '../../utils/userMessages';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Browser-safe email capability state. Delivery and verification providers are selected by Super Admin.
const DEFAULT_EMAIL_SETTINGS = {
    enabled: false,
    provider: 'smtp',
    fromEmail: '',
    fromName: 'TELCOFAIR',
    replyTo: '',
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userRole, setUserRole] = useState(null);
    const [companyData, setCompanyData] = useState(null);
    const [emailSettings, setEmailSettings] = useState(DEFAULT_EMAIL_SETTINGS);
    const [platformSettings, setPlatformSettings] = useState(null);
    const [publicRuntime, setPublicRuntime] = useState(null);

    const activeCompanyId = () => resolveCompanyId({ companyData, userData, currentUser });

    const ensureRegistrationStub = async (firebaseUser, seed = {}) => {
        if (!firebaseUser?.uid) return null;
        const ref = doc(db, 'users', firebaseUser.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) return { id: snap.id, ...snap.data() };

        const email = String(firebaseUser.email || seed.email || '').trim().toLowerCase();
        const firebaseVerified = Boolean(firebaseUser.emailVerified);
        const payload = {
            uid: firebaseUser.uid,
            userId: firebaseUser.uid,
            email,
            emailNormalized: email,
            firstName: seed.firstName || '',
            lastName: seed.lastName || '',
            displayName: seed.displayName || [seed.firstName, seed.lastName].filter(Boolean).join(' '),
            registrationMode: seed.registrationMode || 'individual_draft',
            registrationStage: firebaseVerified ? 'profile_completion' : 'email_verification',
            stage: firebaseVerified ? 'profile_completion' : 'email_verification',
            registrationStatus: firebaseVerified ? 'profile_incomplete' : 'verification_pending',
            registrationIncomplete: true,
            profileCompleted: false,
            emailVerified: firebaseVerified,
            isVerified: firebaseVerified,
            isActive: false,
            isBlocked: false,
            registrationStartedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };
        await setDoc(ref, payload, { merge: true });
        return payload;
    };

    const loadPublicRuntime = async () => {
        try {
            const runtime = await getPublicPlatformRuntime();
            setPublicRuntime(runtime || null);
            return runtime || {};
        } catch (error) {
            console.warn('Platform runtime unavailable:', error?.code || error?.message || error);
            return publicRuntime || {};
        }
    };

    useEffect(() => { loadPublicRuntime(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);


    // Do not prompt unexpectedly. If the user already granted browser
    // notification permission, refresh/save their FCM token automatically.
    useEffect(() => {
        if (!currentUser?.uid || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
        registerPushToken({ uid: currentUser.uid, requestPermission: false }).catch((error) => console.warn('Push token refresh skipped:', error));
    }, [currentUser?.uid]);

    // ============================================
    // FETCH EMAIL SETTINGS
    // ============================================

    const fetchEmailSettings = async (explicitCompanyId) => {
        const companyId = explicitCompanyId || activeCompanyId();
        if (!companyId || !auth.currentUser) {
            setEmailSettings(DEFAULT_EMAIL_SETTINGS);
            return DEFAULT_EMAIL_SETTINGS;
        }
        try {
            const result = await getIntegrationSettings(companyId);
            const data = { ...DEFAULT_EMAIL_SETTINGS, ...(result?.settings?.email || {}) };
            setEmailSettings(data);
            return data;
        } catch (error) {
            console.error('Error fetching email integration settings:', error);
            setEmailSettings(DEFAULT_EMAIL_SETTINGS);
            return DEFAULT_EMAIL_SETTINGS;
        }
    };

    // ============================================
    // FETCH OPTIONAL PLATFORM SETTINGS FROM FIRESTORE
    // ============================================

    const normalizePlatformSettings = (snap, source) => {
        if (!snap?.exists?.()) return null;
        const raw = snap.data() || {};
        const value = raw.value || {};
        const general = value.general || raw.general || {};
        const branding = value.branding || raw.branding || general.branding || {};
        const merged = { id: snap.id, source, ...raw, ...value, general: { ...general }, branding: { ...branding } };
        const platformLogoUrl = getPlatformLogoUrl(merged);
        return {
            ...merged,
            platformLogoUrl,
            platformLogo: platformLogoUrl,
            logoUrl: platformLogoUrl || merged.logoUrl || '',
            logo: platformLogoUrl || merged.logo || '',
            platformName: merged.platformName || merged.appName || general.appName || general.companyName || branding.appName || branding.companyName || 'TELCOFAIR',
        };
    };

    const fetchPlatformSettings = async () => {
        try {
            // Super Admin branding is authoritative on public Login/Register.
            // Compatibility sources are retained for older TELCOFAIR builds.
            const candidates = [
                { ref: doc(db, 'systemSettings', 'app_settings'), source: 'systemSettings/app_settings' },
                { ref: doc(db, 'systemSettings', 'branding'), source: 'systemSettings/branding' },
                { ref: doc(db, 'systemConfig', 'platform'), source: 'systemConfig/platform' },
                { ref: doc(db, 'settings', 'platform'), source: 'settings/platform' },
                { ref: doc(db, 'platformSettings', 'global'), source: 'platformSettings/global' },
            ];
            for (const candidate of candidates) {
                const snap = await getDoc(candidate.ref);
                const data = normalizePlatformSettings(snap, candidate.source);
                if (data && getPlatformLogoUrl(data)) {
                    setPlatformSettings(data);
                    return data;
                }
                if (data && !platformSettings) setPlatformSettings(data);
            }
            return null;
        } catch (error) {
            console.error('Error fetching platform settings:', error);
            return null;
        }
    };

    // ============================================
    // SEND EMAIL VERIFICATION
    // ============================================

    const sendVerificationEmail = async (email, firstName, lastName, targetUid = null) => {
        const user = auth.currentUser || currentUser;
        const uid = targetUid || user?.uid;
        if (!uid) return { success: false, error: 'No authenticated user is available.' };
        try {
            const runtime = publicRuntime || await loadPublicRuntime();
            const provider = runtime?.auth?.verificationProvider || 'firebase_auth';
            if (provider === 'firebase_auth') {
                if (!user || user.uid !== uid) {
                    return { success: false, error: 'Firebase verification can only be resent by the signed-in account.' };
                }
                await user.reload();
                if (user.emailVerified) {
                    await setDoc(doc(db, 'users', uid), {
                        emailVerified: true,
                        isVerified: true,
                        verificationMethod: 'firebase_auth',
                        verificationDeliveryStatus: 'already_verified',
                        verificationDeliveryError: null,
                        updatedAt: serverTimestamp(),
                    }, { merge: true });
                    return { success: true, method: 'firebase_auth', alreadyVerified: true };
                }
                try {
                    // Do not force a continue URL. A custom ActionCodeSettings URL
                    // is valid only when its domain is authorized in Firebase Auth.
                    // The native hosted flow is reliable across deployed domains and
                    // verification state is reloaded when the user returns.
                    await sendEmailVerification(user);
                    await setDoc(doc(db, 'users', uid), {
                        verificationMethod: 'firebase_auth',
                        verificationDeliveryStatus: 'sent',
                        verificationDeliveryError: null,
                        verificationLastSentAt: serverTimestamp(),
                        updatedAt: serverTimestamp(),
                    }, { merge: true });
                    return { success: true, method: 'firebase_auth' };
                } catch (deliveryError) {
                    const message = friendlyError(deliveryError, 'Firebase could not send the verification email. Please check the Firebase Authentication email template and Authorized Domains.');
                    await setDoc(doc(db, 'users', uid), {
                        verificationMethod: 'firebase_auth',
                        verificationDeliveryStatus: 'failed',
                        verificationDeliveryError: message,
                        updatedAt: serverTimestamp(),
                    }, { merge: true }).catch(() => {});
                    return { success: false, method: 'firebase_auth', error: message, code: deliveryError?.code || '' };
                }
            }
            if (provider === 'whatsapp') {
                const userSnap = await getDoc(doc(db, 'users', uid));
                const profile = userSnap.exists() ? userSnap.data() || {} : {};
                const phone = profile.phone || '';
                if (!phone) return { success: false, error: 'Add a WhatsApp phone number before requesting verification.' };
                const result = await requestWhatsAppOtp(phone, 'verification');
                return { success: true, method: 'whatsapp', data: result };
            }
            if (provider === 'manual') {
                return { success: true, method: 'manual', pendingManualVerification: true };
            }
            const result = await sendProfessionalVerificationEmail({
                uid,
                origin: typeof window !== 'undefined' ? window.location.origin : '',
                displayName: [firstName, lastName].filter(Boolean).join(' '),
                email: email || user?.email || '',
            });
            return { success: true, method: 'professional_email', data: result };
        } catch (error) {
            console.error('Send verification error:', error);
            return { success: false, error: friendlyError(error, 'Unable to send the verification email. Please try again.') };
        }
    };

    // ============================================
    // TRANSACTIONAL EMAIL THROUGH CONFIGURED PROVIDER
    // ============================================

    const sendWelcomeEmail = async (email, firstName, lastName, companyName, explicitCompanyId) => {
        const companyId = explicitCompanyId || activeCompanyId();
        if (!companyId) return { success: false, error: 'Company context is not available.' };
        try {
            const displayName = [firstName, lastName].filter(Boolean).join(' ') || 'there';
            const result = await sendOperationEmail(companyId, 'welcome', email, {
                firstName: firstName || displayName.split(' ')[0],
                fullName: displayName,
                companyName: companyName || '',
            });
            return { success: true, data: result };
        } catch (error) {
            console.error('Send welcome error:', error);
            return { success: false, error: friendlyError(error, 'The email could not be sent. Please try again.') };
        }
    };

    const sendRegistrationReminder = async (email, firstName, companyName, explicitCompanyId) => {
        const companyId = explicitCompanyId || activeCompanyId();
        if (!companyId) return { success: false, error: 'Company context is not available.' };
        try {
            const result = await sendOperationEmail(companyId, 'registration_completion', email, {
                firstName: firstName || 'there',
                companyName: companyName || '',
                registrationLink: `${window.location.origin}/register`,
            });
            return { success: true, data: result };
        } catch (error) {
            console.error('Send reminder error:', error);
            return { success: false, error: friendlyError(error, 'The email could not be sent. Please try again.') };
        }
    };

    // ============================================
    // CHECK EMAIL VERIFICATION STATUS
    // ============================================

    const checkEmailVerification = async (uid) => {
        try {
            const runtime = publicRuntime || await loadPublicRuntime();
            const provider = runtime?.auth?.verificationProvider || 'firebase_auth';

            if (provider === 'firebase_auth') {
                const firebaseUser = auth.currentUser;
                if (!firebaseUser || (uid && String(firebaseUser.uid) !== String(uid))) {
                    return { verified: false, provider };
                }
                await firebaseUser.reload();
                return { verified: Boolean(auth.currentUser?.emailVerified), provider };
            }

            const targetUid = uid || auth.currentUser?.uid;
            if (!targetUid) return { verified: false, provider };
            const userDoc = await getDoc(doc(db, 'users', targetUid));
            if (!userDoc.exists()) return { verified: false, provider };
            const data = userDoc.data() || {};
            const verified = provider === 'whatsapp'
                ? Boolean(data.whatsappVerified || data.verificationOverride)
                : provider === 'manual'
                    ? Boolean(data.verificationOverride || data.isVerified)
                    : Boolean(data.emailVerified || data.verificationOverride || data.isVerified);
            return { verified, provider };
        } catch (error) {
            console.error('Check verification error:', error);
            return { verified: false };
        }
    };

    // ============================================
    // REGISTER COMPANY WITH PROFILE PICTURE
    // ============================================

    const registerCompany = async () => {
        // Legacy direct provisioning is intentionally disabled. The current
        // /register workflow persists an incomplete profile first, enforces
        // verification/subscription state, and creates the company only through
        // that resumable lifecycle. Keeping a second provisioning path here was
        // able to create contradictory user/company records.
        throw new Error('Direct company provisioning is disabled. Continue through the TELCOFAIR registration flow.');
    };

    // ============================================
    // REGISTER TEAM MEMBER WITH PROFILE PICTURE
    // ============================================

    const registerTeamMember = async (memberData) => {
        try {
            console.log('📝 registerTeamMember called with:', memberData);
            const companyId = memberData.companyId || activeCompanyId();
            if (!companyId) throw new Error('Company ID is required to add a team member.');

            // Create team members with the Admin SDK through a callable function.
            // This avoids createUserWithEmailAndPassword() replacing the currently
            // signed-in company administrator session in the browser.
            const result = await createCompanyMember(companyId, {
                password: memberData.password,
                role: memberData.role || 'employee',
                permissions: memberData.permissions || ['read'],
                profile: {
                    email: memberData.email,
                    displayName: `${memberData.firstName || ''} ${memberData.lastName || ''}`.trim(),
                    firstName: memberData.firstName || '',
                    lastName: memberData.lastName || '',
                    phoneNumber: memberData.phone || '',
                    dialCode: memberData.dialCode || '',
                    designation: memberData.designation || '',
                    department: memberData.department || '',
                    profileImage: memberData.profileImage || null,
                    photoURL: memberData.profileImage || null,
                    status: 'pending',
                },
            });

            const createdUid = result?.userId || result?.uid;
            console.log('✅ Team member created server-side:', createdUid);
            if (result?.verificationWarning) {
                toast.error(result.verificationWarning, { duration: 6000 });
            } else {
                toast.success(`Account created! ${memberData.firstName || 'The team member'} needs to complete account verification.`);
            }
            return { uid: createdUid, email: result?.email || memberData.email, verificationWarning: result?.verificationWarning || '' };
        } catch (error) {
            console.error('❌ Team member registration error:', error);
            toast.error(friendlyError(error, 'Failed to add team member.'));
            throw error;
        }
    };

    // ============================================
    // UPDATE USER PROFILE
    // ============================================

    const updateUserProfile = async (uid, updates = {}) => {
        try {
            if (!currentUser?.uid || String(uid || '') !== String(currentUser.uid)) {
                throw new Error('You can only update your own profile from the Company Portal.');
            }

            // Client profile editing must never be able to change authorization,
            // subscription, verification, registration, ownership or tenant linkage.
            // Firestore rules must enforce the same policy server-side.
            const protectedFields = new Set([
                'uid', 'userId', 'email', 'emailNormalized', 'companyId', 'companyName',
                'role', 'userRole', 'permissions', 'isOwner', 'isAdmin', 'isApproved',
                'isActive', 'isBlocked', 'isVerified', 'emailVerified', 'whatsappVerified',
                'verificationOverride', 'verificationMethod', 'verificationDeliveryStatus',
                'registrationMode', 'registrationStage', 'registrationStatus',
                'registrationIncomplete', 'profileCompleted', 'paymentStatus', 'seatStatus',
                'subscription', 'subscriptionStatus', 'registrationPackageId',
            ]);
            const safeUpdates = Object.fromEntries(
                Object.entries(updates || {}).filter(([key, value]) => !protectedFields.has(key) && value !== undefined)
            );
            if (!Object.keys(safeUpdates).length) {
                throw new Error('No editable profile fields were provided.');
            }

            const userRef = doc(db, 'users', currentUser.uid);
            await updateDoc(userRef, {
                ...safeUpdates,
                updatedAt: serverTimestamp(),
            });

            const authUpdates = {};
            if (safeUpdates.displayName) authUpdates.displayName = safeUpdates.displayName;
            if (safeUpdates.profileImage || safeUpdates.photoURL) authUpdates.photoURL = safeUpdates.profileImage || safeUpdates.photoURL;
            if (Object.keys(authUpdates).length > 0) {
                await updateProfile(currentUser, authUpdates);
            }

            setUserData(prev => ({ ...prev, ...safeUpdates }));
            toast.success('Profile updated successfully!');
            return { success: true };
        } catch (error) {
            console.error('Update profile error:', error);
            toast.error(friendlyError(error, 'Failed to update profile'));
            throw error;
        }
    };

    // ============================================
    // UPDATE COMPANY PROFILE
    // ============================================

    const updateCompanyProfile = async (companyId, updates) => {
        try {
            await updateCompanyProfileBackend(companyId, updates);
            setCompanyData(prev => ({ ...prev, ...updates }));
            toast.success('Company profile updated successfully!');
            return { success: true };
        } catch (error) {
            console.error('Update company error:', error);
            toast.error(friendlyError(error, 'Failed to update company profile'));
            throw error;
        }
    };

    // ============================================
    // APPROVE TEAM MEMBER
    // ============================================

    const approveTeamMember = async (userId) => {
        try {
            const companyId = activeCompanyId();
            if (!companyId) throw new Error('Company context is not available.');
            const userRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userRef);
            if (!userDoc.exists()) {
                toast.error('User not found');
                return false;
            }
            const member = userDoc.data();
            if (!member.isVerified && !member.emailVerified) {
                toast.error('User must complete account verification before approval');
                return false;
            }
            await approveCompanyJoinRequest(companyId, userId);
            toast.success('Team member approved. The reserved subscription seat is now active.');
            await refreshCompanyData(companyId);
            return true;
        } catch (error) {
            console.error('❌ Approve error:', error);
            toast.error(friendlyError(error, 'Failed to approve team member'));
            throw error;
        }
    };

    // ============================================
    // DEPARTMENT MANAGEMENT
    // ============================================

    const addDepartment = async (companyId, departmentName) => {
        try {
            await updateCompanyProfileBackend(companyId, {
                departments: arrayUnion(departmentName.trim())
            });
            toast.success(`Department "${departmentName}" added!`);
            return true;
        } catch (error) {
            console.error('❌ Add department error:', error);
            toast.error('Failed to add department');
            throw error;
        }
    };

    const removeDepartment = async (companyId, departmentName) => {
        try {
            await updateCompanyProfileBackend(companyId, {
                departments: arrayRemove(departmentName)
            });
            toast.success(`Department "${departmentName}" removed!`);
            return true;
        } catch (error) {
            console.error('❌ Remove department error:', error);
            toast.error('Failed to remove department');
            throw error;
        }
    };

    // ============================================
    // DESIGNATION MANAGEMENT
    // ============================================

    const addDesignation = async (companyId, designationName) => {
        try {
            await updateCompanyProfileBackend(companyId, {
                designations: arrayUnion(designationName.trim())
            });
            toast.success(`Designation "${designationName}" added!`);
            return true;
        } catch (error) {
            console.error('❌ Add designation error:', error);
            toast.error('Failed to add designation');
            throw error;
        }
    };

    const removeDesignation = async (companyId, designationName) => {
        try {
            await updateCompanyProfileBackend(companyId, {
                designations: arrayRemove(designationName)
            });
            toast.success(`Designation "${designationName}" removed!`);
            return true;
        } catch (error) {
            console.error('❌ Remove designation error:', error);
            toast.error('Failed to remove designation');
            throw error;
        }
    };

    // ============================================
    // GET PENDING MEMBERS
    // ============================================

    const getPendingMembers = async (companyId) => {
        try {
            const usersRef = collection(db, 'users');
            const q = query(
                usersRef, 
                where('companyId', '==', companyId),
                where('isApproved', '==', false)
            );
            const querySnapshot = await getDocs(q);
            
            const members = [];
            querySnapshot.forEach((doc) => {
                members.push({ id: doc.id, ...doc.data() });
            });
            return members;
        } catch (error) {
            console.error('Get pending members error:', error);
            return [];
        }
    };

    // ============================================
    // COMPANY IDENTITY REPAIR / LEGACY COMPATIBILITY
    // ============================================

    const repairCompanyLink = async (firebaseUser, profile) => {
        if (!firebaseUser?.uid) return profile;

        const existingCompanyId = resolveCompanyId({ userData: profile });
        if (existingCompanyId) return profile;

        let linkedCompany = null;

        // Older TELCOFAIR registrations used the Firebase UID as the company
        // document id. This lookup is deterministic and does not leak another
        // tenant's identity into the current session.
        try {
            const directCompany = await getDoc(doc(db, 'companies', firebaseUser.uid));
            if (directCompany.exists()) {
                linkedCompany = { id: directCompany.id, ...directCompany.data() };
            }
        } catch (error) {
            console.warn('Legacy company-id lookup skipped:', error?.message || error);
        }

        // Newer registrations create an independent company document id and
        // store the owner as adminId/adminEmail. Use those owner fields only as
        // a repair path when users/{uid}.companyId is missing.
        if (!linkedCompany) {
            try {
                const lookups = [
                    getDocs(query(collection(db, 'companies'), where('adminId', '==', firebaseUser.uid))),
                ];
                if (firebaseUser.email) {
                    lookups.push(getDocs(query(collection(db, 'companies'), where('adminEmail', '==', firebaseUser.email))));
                }
                const results = await Promise.allSettled(lookups);
                for (const result of results) {
                    if (result.status !== 'fulfilled' || result.value.empty) continue;
                    const snap = result.value.docs[0];
                    linkedCompany = { id: snap.id, ...snap.data() };
                    break;
                }
            } catch (error) {
                console.warn('Owner/company link repair skipped:', error?.message || error);
            }
        }

        if (!linkedCompany?.id) return profile;

        const ownerIds = [linkedCompany.adminId, linkedCompany.ownerId, linkedCompany.createdBy, linkedCompany.userId, linkedCompany.uid]
            .filter(Boolean).map(String);
        const ownerEmails = [linkedCompany.adminEmail, linkedCompany.ownerEmail]
            .filter(Boolean).map((value) => String(value).trim().toLowerCase());
        const explicitOwner = ownerIds.includes(String(firebaseUser.uid))
            || Boolean(firebaseUser.email && ownerEmails.includes(String(firebaseUser.email).trim().toLowerCase()));

        const repaired = {
            ...(profile || {}),
            uid: profile?.uid || firebaseUser.uid,
            userId: profile?.userId || firebaseUser.uid,
            companyId: linkedCompany.id,
            companyName: profile?.companyName || linkedCompany.companyName || linkedCompany.name || '',
            isOwner: profile?.isOwner !== undefined ? profile.isOwner : explicitOwner,
            role: profile?.role || (explicitOwner ? 'admin' : 'employee'),
        };

        try {
            await setDoc(doc(db, 'users', firebaseUser.uid), {
                companyId: linkedCompany.id,
                companyName: repaired.companyName,
                updatedAt: serverTimestamp(),
            }, { merge: true });
        } catch (error) {
            // Reading should still work even when the current rules don't allow
            // this optional data-repair write.
            console.warn('Company link repair could not be persisted:', error?.message || error);
        }

        return repaired;
    };

    // ============================================
    // LOGIN
    // ============================================

    const login = async (email, password, rememberMe) => {
        try {
            const normalizedEmail = String(email || '').trim().toLowerCase();
            await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
            const userCredential = await signInWithEmailAndPassword(auth, normalizedEmail, password);
            const user = userCredential.user;

            let userDoc = await getDoc(doc(db, 'users', user.uid));
            if (!userDoc.exists()) {
                // Recover Firebase Auth identities that were created by an older
                // failed registration before the Firestore profile was written.
                await ensureRegistrationStub(user, { email: normalizedEmail });
                userDoc = await getDoc(doc(db, 'users', user.uid));
            }
            let userData = null;
            
            if (userDoc.exists()) {
                userData = await repairCompanyLink(user, userDoc.data());
                
                if (userData.isBlocked) {
                    await signOut(auth);
                    toast.error('Your account has been blocked. Please contact support.');
                    throw new Error('Account blocked');
                }
                
                const runtime = publicRuntime || await loadPublicRuntime();
                const verificationProvider = runtime?.auth?.verificationProvider || 'firebase_auth';
                const providerVerified = verificationProvider === 'firebase_auth'
                    ? Boolean(user.emailVerified)
                    : verificationProvider === 'whatsapp'
                        ? Boolean(userData.whatsappVerified || userData.isVerified || userData.verificationOverride)
                        : Boolean(userData.emailVerified || userData.isVerified || userData.verificationOverride);
                if (!providerVerified) {
                    const resend = await sendVerificationEmail(user.email, userData.firstName || '', userData.lastName || '', user.uid);
                    await signOut(auth);
                    const channel = verificationProvider === 'whatsapp' ? 'WhatsApp verification' : verificationProvider === 'manual' ? 'manual verification' : 'verification';
                    toast.error(resend?.success && verificationProvider !== 'manual' ? `Please complete ${channel}. A new request has been sent.` : `Please complete ${channel} before signing in.`);
                    throw new Error('Account verification required');
                }

                if (providerVerified && (!userData.isVerified || !userData.emailVerified) && verificationProvider === 'firebase_auth') {
                    await updateDoc(doc(db, 'users', user.uid), {
                        isVerified: true,
                        emailVerified: true,
                        verificationMethod: 'firebase_auth',
                        updatedAt: serverTimestamp(),
                    });
                    userData = { ...userData, isVerified: true, emailVerified: true };
                }

                const registrationStatus = String(userData.registrationStatus || '').toLowerCase();
                if (registrationStatus === 'expired') {
                    await signOut(auth);
                    toast.error('This incomplete registration has expired. Contact TELCOFAIR support or ask Super Admin to reactivate it.');
                    throw new Error('Registration expired');
                }
                if (registrationStatus === 'declined') {
                    await signOut(auth);
                    toast.error('This registration was declined. Contact the company administrator or TELCOFAIR support before trying again.');
                    throw new Error('Registration declined');
                }

                const draftRegistration = userData.profileCompleted === false
                    || userData.registrationIncomplete === true
                    || ['individual_draft', 'join_company_draft', 'create_company_draft'].includes(String(userData.registrationMode || '').toLowerCase());
                const companyPaymentPending = userData.isActive === false
                    && userData.registrationMode === 'create_company'
                    && ['pending_company_subscription', 'pending_payment', 'company_payment_pending', 'pending'].includes(String(userData.registrationStatus || userData.paymentStatus || '').toLowerCase());

                // Incomplete registration and pending new-company payment are resumable
                // states, not account failures. Keep the authenticated Firebase session
                // and let the Login page route the user back to /register.
                if (draftRegistration || companyPaymentPending) {
                    await updateDoc(doc(db, 'users', user.uid), {
                        lastActive: serverTimestamp(),
                        isOnline: true,
                    });
                    setCurrentUser(user);
                    setUserData(userData);
                    setUserRole(userData?.role || 'employee');
                    setCompanyData(null);
                    return {
                        user,
                        requiresRegistration: true,
                        registrationStage: userData.registrationStage || (companyPaymentPending ? 'company_subscription' : 'profile_completion'),
                    };
                }
                
                if (!['admin', 'company_admin'].includes(String(userData.role || '').toLowerCase()) && userData.isApproved === false) {
                    await signOut(auth);
                    toast.error('Your account is pending admin approval. Please wait for approval.');
                    throw new Error('Account pending approval');
                }
                
                if (userData.companyId) {
                    const companyDoc = await getDoc(doc(db, 'companies', userData.companyId));
                    if (!companyDoc.exists()) {
                        await signOut(auth);
                        toast.error('Your company profile is unavailable. Contact TELCOFAIR support.');
                        throw new Error('Company profile unavailable');
                    }
                    if (companyDoc.exists()) {
                        const loadedCompany = companyDoc.data();
                        if (loadedCompany.isBlocked === true || loadedCompany.isDeleted === true || loadedCompany.archived === true) {
                            await signOut(auth);
                            toast.error('Your company access has been disabled by TELCOFAIR Super Admin.');
                            throw new Error('Company access disabled');
                        }
                        const subscription = loadedCompany.subscription || {};
                        const subscriptionStatus = String(subscription.status || loadedCompany.subscriptionStatus || '').toLowerCase();
                        const hasSubscriptionContract = Boolean(subscription.planId || subscription.packageId || subscriptionStatus);
                        const expiryValue = subscription.expiryDate || subscription.expiresAt;
                        const expiryDate = expiryValue?.toDate ? expiryValue.toDate() : expiryValue?.seconds ? new Date(expiryValue.seconds * 1000) : expiryValue ? new Date(expiryValue) : null;
                        const expiredByDate = expiryDate instanceof Date && !Number.isNaN(expiryDate.getTime()) && expiryDate.getTime() < Date.now();
                        const blockedSubscription = hasSubscriptionContract && (expiredByDate || ['expired', 'cancelled', 'canceled', 'suspended', 'pending_payment', 'inactive'].includes(subscriptionStatus));
                        if (blockedSubscription) {
                            const canRenew = userData.isOwner === true || ['admin', 'company_admin'].includes(String(userData.role || '').toLowerCase());
                            if (!canRenew) {
                                await signOut(auth);
                                toast.error(expiredByDate || subscriptionStatus === 'expired' ? 'Your company subscription has expired. Ask your company administrator to renew it.' : 'Your company subscription is not active. Ask your company administrator to renew or reactivate the plan.');
                                throw new Error('Company subscription unavailable');
                            }
                            toast.error('Your company subscription requires attention. You can sign in only to renew or manage the subscription.', { duration: 5000 });
                        }
                        setCompanyData({ id: companyDoc.id, companyId: loadedCompany.companyId || companyDoc.id, ...loadedCompany });
                    }
                }
            }

            if (!userData) {
                await signOut(auth).catch(() => {});
                throw new Error('Your account profile could not be loaded. Please try again.');
            }

            await setDoc(doc(db, 'users', user.uid), {
                lastActive: serverTimestamp(),
                isOnline: true,
                updatedAt: serverTimestamp(),
            }, { merge: true });

            setCurrentUser(user);
            setUserData(userData);
            setUserRole(userData?.role || 'employee');

            toast.success(`Welcome ${userData?.displayName || 'User'}!`);
            return user;
        } catch (error) {
            console.error('Login error:', error);
            const message = friendlyError(error, 'Unable to sign in. Please check your details and try again.');
            toast.error(message);
            throw new Error(message);
        }
    };

    const requestWhatsAppLoginOtp = async (phone) => {
        try {
            const runtime = publicRuntime || await loadPublicRuntime();
            if (runtime?.auth?.provider !== 'whatsapp') throw new Error('WhatsApp authentication is not enabled by Super Admin.');
            return await requestWhatsAppOtp(phone, 'authentication');
        } catch (error) {
            throw new Error(friendlyError(error, 'Unable to send the WhatsApp login code.'));
        }
    };

    const loginWithWhatsAppOtp = async (phone, otp) => {
        try {
            const runtime = publicRuntime || await loadPublicRuntime();
            if (runtime?.auth?.provider !== 'whatsapp') throw new Error('WhatsApp authentication is not enabled by Super Admin.');
            const result = await verifyWhatsAppOtp(phone, otp, 'authentication');
            const user = auth.currentUser;
            if (!user) throw new Error('WhatsApp sign-in could not complete. Please contact TELCOFAIR support.');
            const snap = await getDoc(doc(db, 'users', user.uid));
            if (!snap.exists()) throw new Error('Your TELCOFAIR user profile was not found.');
            const profile = await repairCompanyLink(user, snap.data());
            if (profile.isBlocked) { await signOut(auth); throw new Error('Your account has been blocked.'); }
            setCurrentUser(user); setUserData(profile); setUserRole(profile.role || 'employee');
            if (profile.companyId) { const companySnap = await getDoc(doc(db, 'companies', profile.companyId)); if (companySnap.exists()) setCompanyData({ id: companySnap.id, ...companySnap.data() }); }
            return { user, userData: profile, requiresRegistration: profile.registrationIncomplete === true || profile.profileCompleted === false, gateway: result };
        } catch (error) {
            throw new Error(friendlyError(error, 'Unable to sign in with WhatsApp.'));
        }
    };

    const verifyAccountWithWhatsAppOtp = async (phone, otp) => {
        const result = await verifyWhatsAppOtp(phone, otp, 'verification');
        const uid = auth.currentUser?.uid;
        if (!uid) throw new Error('Your account session has expired.');
        await setDoc(doc(db, 'users', uid), { whatsappVerified: true, isVerified: true, verificationMethod: 'whatsapp', verifiedAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
        return result;
    };

    // ============================================
    // LOGOUT
    // ============================================

    const logout = async () => {
        try {
            if (currentUser) {
                await updateDoc(doc(db, 'users', currentUser.uid), {
                    isOnline: false,
                    lastActive: serverTimestamp()
                });
            }
            await signOut(auth);
            setCurrentUser(null);
            setUserData(null);
            setUserRole(null);
            setCompanyData(null);
            toast.success('Logged out successfully');
        } catch (error) {
            console.error('Logout error:', error);
            toast.error('Failed to logout');
            throw error;
        }
    };

    // ============================================
    // RESET PASSWORD
    // ============================================

    const resetPassword = async (email) => {
        try {
            const runtime = publicRuntime || await loadPublicRuntime();
            const delivery = runtime?.auth?.passwordResetProvider || 'firebase_auth';
            if (delivery === 'firebase_auth') {
                const result = { success: true, message: 'If an account exists, a password reset email has been sent.' };
                try {
                    await sendPasswordResetEmail(auth, email);
                } catch (error) {
                    if (!['auth/user-not-found', 'auth/user-not-found.'].includes(String(error?.code || '').toLowerCase())) throw error;
                }
                toast.success(result.message);
                return result;
            }
            const result = await requestProfessionalPasswordResetEmail(email, typeof window !== 'undefined' ? window.location.origin : '');
            toast.success(result?.message || 'If an account exists, a password reset email has been sent.');
            return result;
        } catch (error) {
            console.error('Reset password error:', error);
            const message = friendlyError(error, 'Unable to request a password reset. Please try again.');
            toast.error(message);
            throw new Error(message);
        }
    };

    // ============================================
    // GET USER DATA
    // ============================================

    const getUserData = async (uid) => {
        try {
            const userDoc = await getDoc(doc(db, 'users', uid));
            if (userDoc.exists()) {
                return userDoc.data();
            }
            return null;
        } catch (error) {
            console.error('Get user data error:', error);
            return null;
        }
    };

    // ============================================
    // GET COMPANY DATA
    // ============================================

    const getCompanyData = async (companyId) => {
        try {
            if (!companyId) return null;
            const companyDoc = await getDoc(doc(db, 'companies', companyId));
            if (companyDoc.exists()) {
                return { id: companyDoc.id, ...companyDoc.data() };
            }
            return null;
        } catch (error) {
            console.error('Get company data error:', error);
            return null;
        }
    };

    const refreshCompanyData = async (companyIdOverride = null) => {
        const resolvedCompanyId = companyIdOverride || userData?.companyId || companyData?.companyId || companyData?.id;
        if (!resolvedCompanyId) return null;
        const company = await getCompanyData(resolvedCompanyId);
        if (company) setCompanyData(company);
        return company;
    };

    // ============================================
    // GET TEAM MEMBERS
    // ============================================

    const getTeamMembers = async (companyId) => {
        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('companyId', '==', companyId));
            const querySnapshot = await getDocs(q);
            
            const members = [];
            querySnapshot.forEach((doc) => {
                members.push({ id: doc.id, ...doc.data() });
            });
            return members;
        } catch (error) {
            console.error('Get team members error:', error);
            return [];
        }
    };

    // Keep the authenticated tenant identity live. This removes stale logo/cover
    // state after an upload and also reflects edits made in another tab/session.
    useEffect(() => {
        const companyId = userData?.companyId;
        if (!companyId) return undefined;
        const unsubscribeCompany = onSnapshot(doc(db, 'companies', String(companyId)), (snap) => {
            if (!snap.exists()) return;
            setCompanyData({ id: snap.id, ...snap.data() });
        }, (error) => console.warn('Live company branding unavailable:', error?.message || error));
        return unsubscribeCompany;
    }, [userData?.companyId]);

    // Public auth branding listens to the Super Admin branding mirror so a logo
    // replacement appears immediately without borrowing any tenant/company logo.
    useEffect(() => {
        const ref = doc(db, 'systemSettings', 'branding');
        const unsubscribePlatform = onSnapshot(ref, (snap) => {
            const data = normalizePlatformSettings(snap, 'systemSettings/branding');
            if (data && getPlatformLogoUrl(data)) {
                setPlatformSettings(data);
                if (typeof window !== 'undefined') window.localStorage.setItem('telcofair_platform_logo', getPlatformLogoUrl(data));
            }
        }, () => {});
        return unsubscribePlatform;
        // normalizePlatformSettings is intentionally stable for this provider lifetime.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ============================================
    // AUTH STATE OBSERVER
    // ============================================

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setLoading(true);
            
            if (user) {
                console.log('🔥 Auth state changed - User logged in:', user.uid);
                setCurrentUser(user);
                
                try {
                    let loadedUserData = await getUserData(user.uid);
                    if (!loadedUserData) {
                        loadedUserData = await ensureRegistrationStub(user).catch((error) => {
                            console.warn('Registration profile recovery failed:', error?.message || error);
                            return null;
                        });
                    }
                    const userData = await repairCompanyLink(user, loadedUserData);
                    setUserData(userData || null);
                    setUserRole(userData?.role || 'user');
                    
                    if (userData?.companyId) {
                        // Load the secure per-company integration profile once identity is known.
                        fetchEmailSettings(userData.companyId);
                        const company = await getCompanyData(userData.companyId);
                        if (company) {
                            console.log('🏢 Company data loaded:', company);
                            setCompanyData(company);
                        } else if (userData.companyName) {
                            setCompanyData({
                                id: userData.companyId,
                                companyName: userData.companyName,
                                ...userData
                            });
                        }
                    } else {
                        setCompanyData(null);
                    }
                } catch (error) {
                    console.error('Error loading user data:', error);
                }
            } else {
                console.log('🔥 Auth state changed - User logged out');
                setCurrentUser(null);
                setUserData(null);
                setUserRole(null);
                setCompanyData(null);
            }
            
            setLoading(false);
        });

        fetchPlatformSettings();

        return unsubscribe;
    }, []);

    // ============================================
    // CONTEXT VALUE
    // ============================================

    const value = {
        currentUser,
        userData,
        userRole,
        companyData,
        loading,
        emailSettings,
        platformSettings,
        publicRuntime,
        registerCompany,
        registerTeamMember,
        approveTeamMember,
        addDepartment,
        removeDepartment,
        addDesignation,
        removeDesignation,
        getPendingMembers,
        login,
        requestWhatsAppLoginOtp,
        loginWithWhatsAppOtp,
        verifyAccountWithWhatsAppOtp,
        logout,
        resetPassword,
        sendVerificationEmail,
        sendWelcomeEmail,
        sendRegistrationReminder,
        checkEmailVerification,
        updateUserProfile,
        updateCompanyProfile,
        getUserData,
        getCompanyData,
        refreshCompanyData,
        getTeamMembers,
        fetchEmailSettings,
        fetchPlatformSettings,
        isAuthenticated: !!currentUser,
        isAdmin: userData?.role === 'admin' || userData?.role === 'company_admin',
        isCompanyAdmin: userData?.role === 'company_admin',
        isTeamMember: userData?.role === 'employee' || userData?.role === 'manager' || userData?.role === 'viewer',
        isManager: userData?.role === 'manager',
        isViewer: userData?.role === 'viewer'
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;